import type { ReactNode } from 'react';

import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import type { Address, CreditsafeRecord, CreditsafeStatus, OrganisationWithVatNumberClient } from '~/api/clients/types';

import { useRefreshVatInformation } from '~/api/clients';
import { Button, Card, CountryFlag, ExternalLink, Icon, Infotip, Spinner, StatusTag, Tooltip } from '~/components';
import { CreditRating } from '~/features/clients';
import { useIntl } from '~/hooks/useIntl';
import { srOnlyStyle } from '~/styles/utilities.css';
import { selectBestRelativeTimeFormat } from '~/utils/dates';
import { formatVatNumber } from '~/utils/string';
import { toast } from '~/utils/toast';

import {
  addressInfoCountryNameStyle,
  addressInfoCountryStyle,
  addressInfoStyle,
  articleStyle,
  companyNameStyle,
  creditRatingInfoStyle,
  generalInfoDetailsStyle,
  generalInfoHeaderStyle,
  generalInfoIconStyle,
  generalInfoStyle,
  labelStyle,
  peppolInfoLabelStyle,
  peppolInfoRowStyle,
  peppolInfoStyle,
  removeButtonStyle,
  vatInformationLastChangedStyle,
} from './OrganisationInfoCard.css';

export const OrganisationInfoCard = ({ children, onRemove }: { children: ReactNode; onRemove?: () => void }) => {
  const { t } = useTranslation(['clients']);

  return (
    <Card.Root className={articleStyle} variant="primary-accent">
      {onRemove && (
        <Tooltip content={t('clients:create.company.changeVatNumber')}>
          <Button
            aria-label={t('clients:create.company.changeVatNumber')}
            className={removeButtonStyle}
            data-remove-action
            emphasis="tertiary"
            icon="CloseSmall"
            onClick={onRemove}
          />
        </Tooltip>
      )}

      {children}
    </Card.Root>
  );
};

export const OrganisationInfoGeneral = ({
  companyName,
  otherNumber,
  refreshVatInformationButton = null,
  vatInformationLastUpdatedAt = null,
  vatNumber,
}: {
  companyName: string;
  otherNumber: string | null;
  refreshVatInformationButton?: ReactNode;
  vatInformationLastUpdatedAt?: string | null;
  vatNumber: string | null;
}) => {
  const { formatRelativeTime } = useIntl();
  const { t } = useTranslation(['clients']);

  return (
    <div className={generalInfoStyle}>
      <div className={generalInfoHeaderStyle}>
        <span className={generalInfoIconStyle}>
          <Icon name="Company" />
        </span>

        <div className={generalInfoDetailsStyle}>
          <span className={srOnlyStyle}>{t('clients:type.organisation')}</span>
          <span className={companyNameStyle}>{companyName}</span>

          {vatNumber && <span>{formatVatNumber(vatNumber)}</span>}

          {otherNumber && <span>{otherNumber}</span>}
        </div>

        {refreshVatInformationButton}
      </div>

      {vatInformationLastUpdatedAt && (
        <span className={vatInformationLastChangedStyle}>
          {t('clients:detail.info.refreshVatInformation.lastUpdated', {
            timeAgo: formatRelativeTime(...selectBestRelativeTimeFormat(vatInformationLastUpdatedAt)),
          })}
        </span>
      )}
    </div>
  );
};

export const OrganisationRefreshVatInformationButton = ({ client }: { client: OrganisationWithVatNumberClient }) => {
  const { t } = useTranslation(['clients', 'common']);

  const hasRecentlyBeenRefreshed = dayjs().diff(client.vatInformationLastUpdatedAt, 'days') < 7;
  const refreshVatInformationMutation = useRefreshVatInformation();

  return (
    <Tooltip
      content={
        <Trans
          components={{
            kboLink: (
              <ExternalLink href={`https://kbopub.economie.fgov.be/kbopub/zoeknummerform.html?nummer=${client.vatNumber.slice(2)}`} />
            ),
          }}
          i18nKey={
            hasRecentlyBeenRefreshed
              ? 'clients:detail.info.refreshVatInformation.tooltip.notRefreshable'
              : 'clients:detail.info.refreshVatInformation.tooltip.refreshable'
          }
        />
      }
    >
      <Button
        aria-label={t('clients:detail.info.refreshVatInformation.ariaLabel')}
        disabled={hasRecentlyBeenRefreshed}
        emphasis="tertiary"
        icon="Sync"
        loading={refreshVatInformationMutation.isPending}
        onClick={() => refreshVatInformationMutation.mutate(client.id, { onError: () => toast.danger({ description: t('common:error') }) })}
      />
    </Tooltip>
  );
};

export const OrganisationInfoAddress = ({ address }: { address: Omit<Address, 'type'> }) => {
  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['clients']);

  return (
    <div className={addressInfoStyle}>
      <span className={labelStyle}>{t('clients:addressType.headquarters')}</span>

      <span>
        {address.street} {address.number}
      </span>

      <span>
        {address.zipCode} {address.city}
      </span>

      <div className={addressInfoCountryStyle}>
        <CountryFlag countryCode={address.countryCode} />

        <span className={addressInfoCountryNameStyle}>{displayNameOfCountry(address.countryCode)}</span>
      </div>
    </div>
  );
};

export const OrganisationInfoCreditRating = ({
  creditsafeRecord,
  creditsafeStatus,
}: {
  creditsafeRecord: CreditsafeRecord | null;
  creditsafeStatus: CreditsafeStatus;
}) => {
  return (
    <div className={creditRatingInfoStyle}>
      <CreditRating creditRating={creditsafeRecord?.creditRating ?? undefined} creditsafeStatus={creditsafeStatus} />
    </div>
  );
};

export const OrganisationInfoPeppol = ({
  peppolId,
  queryStatus = 'success',
}: {
  peppolId: string | null;
  queryStatus?: 'success' | 'pending' | 'error';
}) => {
  const { t } = useTranslation(['clients']);

  const isNotRegistered = queryStatus === 'success' && peppolId === null;

  return (
    <div className={peppolInfoStyle} data-peppol>
      <div className={peppolInfoLabelStyle}>
        <span className={labelStyle}>{t('clients:peppol.status.label')}</span>
        {queryStatus === 'success' && !isNotRegistered && (
          <Infotip>
            <Trans components={{ faqLink: <ExternalLink /> }} i18nKey="clients:peppol.status.tooltip.active" />
          </Infotip>
        )}
      </div>

      <div className={peppolInfoRowStyle}>
        {match(queryStatus)
          .with('pending', () => (
            <>
              <Spinner size={24} />
              <span>{t('clients:peppol.status.pending')}</span>
            </>
          ))
          .with('success', () =>
            isNotRegistered ? (
              <StatusTag icon="Peppol" label={t('clients:peppol.status.notRegistered')} status="warning" />
            ) : (
              <StatusTag icon="Peppol" label={t('clients:peppol.status.active')} status="success" />
            ),
          )
          .with('error', () => <StatusTag icon="Error" label={t('clients:peppol.status.error')} status="danger" />)
          .exhaustive()}

        {isNotRegistered && <Infotip>{t('clients:peppol.status.tooltip.notRegistered')}</Infotip>}
      </div>
    </div>
  );
};
